import { useAppDispatch, useAppSelector } from "../../store";
import { LocatorImageType } from "../../types/LocatorTypes";
import { updateLocatorFilters, updateUploadedLocatorData } from "../../store/reducers/LocatorsReducers";
import { setIsUploadStarted } from "../../store/reducers/DatasetReducer";
import {
  getLocatorFilters,
  getLocatorsLengths,
  getLocatorsTypes,
  getLocatorFetchStatus,
} from "../../store/selectors/LocatorSelectors";
import {
  createLocatorLengthAction,
  createLocatorTypeAction,
  deleteLocatorLengthAction,
  deleteLocatorTypeAction,
  updateLocatorTypeAction,
  getLocatorsLengthsAction,
  getLocatorsTypesAction,
} from "../../store/actions/LocatorsActions";
import GridNavigation, { FilterConfig } from "./GridNavigation";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const LocatorGridNavigation = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const locatorFilters = useAppSelector(getLocatorFilters);
  const locatorLengths = useAppSelector(getLocatorsLengths);
  const locatorTypes = useAppSelector(getLocatorsTypes);
  const locatorFetchStatus = useAppSelector(getLocatorFetchStatus);
  const isLocatorDataFetching = locatorFetchStatus === "loading";

  useEffect(() => {
    if (locatorLengths || isLocatorDataFetching) return;
    dispatch(getLocatorsLengthsAction());
  }, [locatorLengths, isLocatorDataFetching, dispatch]);

  useEffect(() => {
    if (locatorTypes || isLocatorDataFetching) return;
    dispatch(getLocatorsTypesAction());
  }, [locatorTypes, isLocatorDataFetching, dispatch]);

  const onFilterAdd = (newFilter: any) => {
    dispatch(updateLocatorFilters({ ...locatorFilters, ...newFilter }));
    navigate("/dataset/locators/1");
  };

  const onFilterClear = (filterType: "imagetype" | "typeid" | "lengthid") => {
    dispatch(
      updateLocatorFilters({
        ...locatorFilters,
        [filterType]: "undefined",
      })
    );
    navigate("/dataset/locators/1");
  };

  const createNumberFilter = (filter: string) => {
    const filterAsFloat: number = parseFloat(filter);
    const filterExists = locatorLengths?.map(x => x.length).includes(filterAsFloat);
    
    if (filterAsFloat && !filterExists) {
      const formBody: any = [];
      formBody.push(encodeURIComponent('length') + "=" + encodeURIComponent(filterAsFloat));
      dispatch(createLocatorLengthAction(formBody.join("&")));
    }
  };

  const createTextFilter = (filter: string) => {
    const filterExists = locatorTypes?.map(x => x.typename).includes(filter);
    if (!filterExists) {
      const formBody: any = [];
      formBody.push(encodeURIComponent('typename') + "=" + encodeURIComponent(filter));
      dispatch(createLocatorTypeAction(formBody.join("&")));
    }
  };

  const onSelectLocatorImage = (type: string) => {
    dispatch(updateUploadedLocatorData({ imagetype: type as LocatorImageType }));
    dispatch(setIsUploadStarted(true));
  };

  const filters: FilterConfig[] = [
    {
      type: 'image',
      label: 'By image type',
      options: [
        { label: "Render", value: "render" },
        { label: "Photo", value: "photo" },
      ],
      selectedValue: locatorFilters?.imagetype,
      onFilterAdd: (value) => onFilterAdd({ imagetype: value }),
      onFilterClear: () => onFilterClear("imagetype"),
    },
    {
      type: 'length',
      label: 'Filter by Length',
      options: locatorLengths?.map(({ lengthid, length }) => ({
        value: lengthid,
        label: length !== null ? length.toString() : "Unlabeled",
      })) || [],
      selectedValue: locatorFilters?.lengthid,
      onFilterAdd: (value) => onFilterAdd({ lengthid: value }),
      onFilterClear: () => onFilterClear("lengthid"),
      hasCreateNewFilter: true,
      hasEditableFilters: false,
      createFilter: createNumberFilter,
      deleteFilter: (lengthId) => {
        dispatch(deleteLocatorLengthAction({ lengthId }));
      },
    },
    {
      type: 'type',
      label: 'Filter by Type',
      options: locatorTypes?.map(({ typeid, typename }) => ({
        value: typeid,
        label: typename !== null ? typename : "Unlabeled",
      })) || [],
      selectedValue: locatorFilters?.typeid,
      onFilterAdd: (value) => onFilterAdd({ typeid: value }),
      onFilterClear: () => onFilterClear("typeid"),
      hasCreateNewFilter: true,
      hasEditableFilters: true,
      createFilter: createTextFilter,
      deleteFilter: (typeId) => {
        dispatch(deleteLocatorTypeAction({ typeId }));
      },
      editFilter: (typeId, typeName) => {
        dispatch(updateLocatorTypeAction({ typeId, typeName }));
      },
    },
  ];

  const addMenuItems = [
    { label: "Add a photo", value: LocatorImageType.PHOTO },
    { label: "Add a render", value: LocatorImageType.RENDER },
  ];

  return (
    <GridNavigation
      filters={filters}
      onAddItem={onSelectLocatorImage}
      addMenuItems={addMenuItems}
      baseRoute="/dataset/locators"
      showAddMenu={true}
    />
  );
};

export default LocatorGridNavigation; 